<script setup lang="ts">
  import { storeToRefs } from 'pinia';
  import { computed, inject, ref, watchEffect, type Ref } from 'vue';
  import { useRoute, type RouteLocationRaw, type RouteMeta } from 'vue-router';

  import { EnumHeaderList } from '@/helpers/enums/header-lists.enum';
  import { EnumRoutes } from '@/helpers/enums/routes.enum';
  import { useI18 } from '@/plugins';
  import { useAuthStore } from '@/stores/auth.store';
  import { useGroupStore } from '@/stores/groups.store';
  import { useOrganizationStore } from '@/stores/organzation.pinia';
  import { usePermissionsStore } from '@/stores/permissoins.store';
  import type { headerItem } from '@/submodules/himam-common-components/src/helpers/interfaces/header-item.interface';

  const orgStore = useOrganizationStore();
  const { dashboardText, coursesText } = storeToRefs(orgStore);
  const permissionsStore = usePermissionsStore();
  const { checkAuthentication } = permissionsStore;
  const { t } = useI18();
  const authStore = useAuthStore();
  const { user } = storeToRefs(authStore);
  const authItem = computed(() => [
    { ...EnumRoutes.dashboard.root, title: dashboardText.value },
    EnumRoutes.profile.favorite.root,
    { ...EnumRoutes.lms.allCourses, title: coursesText.value },
    {
      ...EnumRoutes.lms.myCourses,
      title: 'course.myCourses',
    },
    {
      ...EnumRoutes.lms.specialization,
      title: 'course.specializations',
    },
  ]);
  const groupStore = useGroupStore();
  const { groups } = storeToRefs(groupStore);
  const headerItems = computed(() => {
    if (user.value) {
      return authItem.value.reduce((acc: headerItem[], item) => {
        if (checkAuthentication(item.meta as RouteMeta))
          if (
            !(item.meta as RouteMeta).shouldHaveGroup ||
            groups.value?.data?.length
          ) {
            acc.push({
              title: (item.meta as RouteMeta).dontUseKey
                ? item.title
                : t(item.title, { courseText: coursesText.value }).toString(),
              to: item.path,
              routeName: item.name,
            });
          }
        return acc;
      }, []);
    } else {
      return EnumHeaderList.value.headerItemsUnAuth.reduce(
        (acc: headerItem[], item) => {
          if (checkAuthentication(item.meta as RouteMeta))
            acc.push({
              title: (item.meta as RouteMeta).dontUseKey
                ? item.title
                : t(item.title, { courseText: coursesText.value }).toString(),
              to: item.path,
              routeName: item.name,
            });
          return acc;
        },
        []
      );
    }
  });
  defineProps<{ rail: boolean }>();
  const activeItem: Ref<RouteLocationRaw[]> = ref([]);
  const path: Ref<string> = inject('path', ref(''));
  const route = useRoute();
  const activeRouteName = computed(() => route.meta.activeRouteName);
  watchEffect(() => {
    let firstMath = '';
    headerItems?.value.forEach((item) => {
      if (!item.children) {
        if (item.routeName === activeRouteName.value) {
          firstMath = item.to as string;
          activeItem.value = [item.to as string];
          return;
        }
        if (
          path.value.startsWith(item.to as string) &&
          (item.to as string)?.length >= firstMath.length
        ) {
          firstMath = item.to as string;
          activeItem.value = [item.to as string];
        }
      } else {
        item.children.forEach((child) => {
          if (item.routeName === activeRouteName.value) {
            firstMath = item.to as string;
            activeItem.value = [item.to as string];
            return;
          }
          if (
            path.value.startsWith(child.to as string) &&
            (child.to as string)?.length >= firstMath.length
          ) {
            firstMath = child.to as string;
            activeItem.value = [child.to as string];
          }
        });
      }
    });
    if (!firstMath.length) activeItem.value = [];
  });
  const scrollUp = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  };
</script>

<template>
  <v-list
    id="menu-drawer-items"
    v-model:selected="activeItem"
    mandatory
    nav
    class="pa-0"
    aria-label="Menu Drawer"
    role="option"
  >
    <template v-for="item in headerItems" :key="item.title">
      <v-list-item
        :active="activeItem?.[0] === item.to"
        density="compact"
        :value="item.to"
        :to="item.to"
        color="primary"
        class="mb-1 rounded-lg"
        :class="activeItem?.[0] === item.to ? 'text-primary' : 'text-gray'"
        active-class="bg-light"
        @click="scrollUp"
      >
        <template #title>
          <div
            :class="!rail ? 'text-wrap' : 'text-no-wrap'"
            class="text-capitalize"
          >
            {{ item.title }}
          </div>
        </template>
      </v-list-item>
    </template>
  </v-list>
</template>

<style lang="scss">
  .button-back {
    opacity: 0;
  }

  .drawer {
    min-width: 61px;
    &:hover {
      .button-back {
        opacity: 1;
        transition: opacity 0.5s;
      }
    }
  }
  .v-navigation-drawer__content {
    /* Width of the scrollbar */
    &::-webkit-scrollbar {
      width: 5px;
    }

    /* Track */
    &::-webkit-scrollbar-track {
      background: #f1f1f1;
    }

    /* Handle */
    &::-webkit-scrollbar-thumb {
      background: #888;
    }

    /* Handle on hover */
    &::-webkit-scrollbar-thumb:hover {
      background: #555;
    }

    /* For Firefox */
    & {
      scrollbar-width: thin;
      scrollbar-color: #888 #f1f1f1;

      /* Handle on hover for Firefox */
      &:hover {
        scrollbar-color: #555 #f1f1f1;
      }
    }
  }

  .nav-drawer {
    .v-list-item--variant-text .v-list-item__overlay {
      background-color: grey !important;
    }
    .v-list-item:hover > .v-list-item__overlay {
      opacity: 0.08 !important;
    }
    .v-list-group__items .v-list-item {
      padding-inline-start: 8px !important;
    }
    .v-list-item__spacer {
      width: 16px !important;
    }
  }
</style>
